import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { getPublisherIdByPortalId } from 'utils/portalUtils';

import { AppsFlyerS2SFormValues } from 'components/Settings/DeveloperSettings/components/AppsFlyerS2S/types';
import { appsFlyerS2SDefaults } from 'components/Settings/DeveloperSettings/DeveloperSettings.constants';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';
import { useUserDetails } from './useUserDetails';

type ApiEventConfig = { enabled: boolean; eventName?: string };

type ApiIntegration = {
  enabled: boolean;
  s2sToken: string;
  appIds: string[];
  events: {
    'payment-success': ApiEventConfig;
    refund: ApiEventConfig;
  };
};

type ApiUpsert = Omit<ApiIntegration, 'enabled'>;

const normalize = (data: ApiIntegration): AppsFlyerS2SFormValues => ({
  enabled: Boolean(data.enabled),
  s2sToken: data.s2sToken ?? '',
  appIds: data.appIds && data.appIds.length > 0 ? data.appIds : [''],
  events: {
    'payment-success': {
      enabled: data.events?.['payment-success']?.enabled ?? true,
      eventName:
        data.events?.['payment-success']?.eventName ??
        appsFlyerS2SDefaults.purchaseEventName
    },
    refund: {
      enabled: data.events?.refund?.enabled ?? true,
      eventName:
        data.events?.refund?.eventName ?? appsFlyerS2SDefaults.refundEventName
    }
  }
});

const toEventBody = (event: {
  enabled: boolean;
  eventName: string;
}): ApiEventConfig => {
  const eventName = event.eventName.trim();
  return eventName
    ? { enabled: event.enabled, eventName }
    : { enabled: event.enabled };
};

const toBody = (values: AppsFlyerS2SFormValues): ApiUpsert => ({
  s2sToken: values.s2sToken.trim(),
  appIds: values.appIds.map((id) => id.trim()).filter(Boolean),
  events: {
    'payment-success': toEventBody(values.events['payment-success']),
    refund: toEventBody(values.events.refund)
  }
});

type UseAppsFlyerIntegrationOptions = {
  enabled?: boolean;
};

export default function useAppsFlyerIntegration(
  currentPublisherId?: string,
  options?: UseAppsFlyerIntegrationOptions
) {
  const axios = useAxios();
  const { portalId } = useParams();
  const { userDetails } = useUserDetails();

  const effectivePublisherId =
    currentPublisherId ||
    (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);
  const publisherHeader = effectivePublisherId
    ? { 'x-publishers': effectivePublisherId }
    : undefined;

  const basePath = `${getApiRouteOrMock(EApiRoutes.INTEGRATION)}/appsflyer`;
  const queryKey = [EQueryKeys.APPSFLYER_INTEGRATION, effectivePublisherId];

  const getIntegration = useQuery<AppsFlyerS2SFormValues | null, Error>({
    ...fetchConfig.general,
    refetchOnReconnect: false,
    queryKey,
    queryFn: async () => {
      try {
        const data: ApiIntegration = await axios.get(
          basePath,
          {},
          publisherHeader
        );
        return normalize(data);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!effectivePublisherId && (options?.enabled ?? true)
  });

  const upsert = useMutation(async (values: AppsFlyerS2SFormValues) => {
    const payload = toBody(values);
    return await axios.put(basePath, payload, publisherHeader);
  });

  const disable = useMutation(async () => {
    return await axios.post(`${basePath}/disable`, {}, publisherHeader);
  });

  return {
    getIntegration,
    upsert,
    disable
  };
}
